import { useSalesEntry } from "contexts/SalesEntryContext";
import DataGrid, { Column } from "devextreme-react/cjs/data-grid";
import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { useKaratSelectList, useOldGoldRate } from "../../services";
import { GetOldGoldRateByKaratIdApi } from "modules/transactions/estimate-payment-receipt/models";
import { apiClient } from "helpers/api_helper";

interface OldGoldModalProps {
  show: boolean;
  onHide: () => void;
}

interface OldGoldEntry {
  id: string;
  material: string;
  kt: string;
  rate: number;
  purity: number;
  netWt: number;
  equivalentWt: number;
  totalAmt: number;
  note: string;
}

interface FormData {
  material: string;
  kt: string;
  rate: string;
  purity: string;
  netWt: string;
  equivalentWt: string;
  totalAmt: string;
  note: string;
}

interface FormErrors {
  material?: string;
  kt?: string;
  rate?: string;
  purity?: string;
  netWt?: string;
  equivalentWt?: string;
  totalAmt?: string;
  note?: string;
}

export const OldGoldModal: React.FC<OldGoldModalProps> = ({ show, onHide }) => {
  const { state, actions } = useSalesEntry();
  const { karatList, loading: karatLoading, error: karatError } = useKaratSelectList();
  const { rateData, loading: rateLoading, error: rateError, getOldGoldRateByKaratId } = useOldGoldRate();
  const [entries, setEntries] = useState<OldGoldEntry[]>([]);
  const [formData, setFormData] = useState<FormData>({
    material: "Gold",
    kt: '',
    rate: "",
    purity: "",
    netWt: "",
    equivalentWt: "",
    totalAmt: "",
    note: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Load existing old gold entries when modal opens
  useEffect(() => {
    if (show && state.oldGoldEntries && state.oldGoldEntries.length > 0) {
      setEntries(state.oldGoldEntries);
    } else if (show) {
      setEntries([]);
    }
  }, [show, state.oldGoldEntries]);

  // Set default karat to 24KT and fetch rate when modal opens or karat list loads
  useEffect(() => {
    if (show && karatList.length > 0) {
      const defaultKarat = karatList.find(karat => karat.text === "24KT");
      apiClient.get(`/api/v1/GoldRateMaster/GetTodaysGoldRateMaster?ktId=${defaultKarat?.value}`).then(v => {
        setTodayGoldRate(v.data.metalRate)
      }).catch((v) => setTodayGoldRate(0))
      if (defaultKarat) {
        setFormData(prev => ({
          ...prev,
          kt: defaultKarat.text,
        }));
        GetOldGoldRateByKaratIdApi(defaultKarat.id);
      }
    }
  }, [show, karatList, GetOldGoldRateByKaratIdApi]);


  const [todayGoldRate, setTodayGoldRate] = useState<any>()



  // Update rate and purity when rateData changes
  useEffect(() => {
    if (rateData) {
      const rate = rateData.metalRate || 0;
      const purity = rateData.purity || 0;
      const rateString = rate >= 0 ? rate.toString() : "";

      setFormData(prev => ({
        ...prev,
        rate: rateString,
        purity: (prev.purity || purity > 0) ? prev.purity : purity.toString()
      }));
    }
  }, [rateData]);

  // Show toast when there's a rate error
  useEffect(() => {
    if (rateError) {
      toast.error(rateError, {
        theme: "colored",
        position: "top-right",
        autoClose: 5000,
      });

      if (!rateData || rateData.metalRate < 0) {
        setFormData(prev => ({
          ...prev,
          rate: "",
          equivalentWt: "",
          totalAmt: ""
        }));
      }
    }
  }, [rateError, rateData]);

  // Auto-calculate equivalent weight and total amount
  useEffect(() => {
    const rate = Number(todayGoldRate) || 0;
    const purity = Number(formData.purity) || 0;
    const netWt = Number(formData.netWt) || 0;

    if (rate > 0 && purity > 0 && netWt > 0) {
      const calculatedEquivalentWt = (netWt * purity) / 100;
      const calculatedTotalAmt = calculatedEquivalentWt * rate;

      setFormData(prev => ({
        ...prev,
        equivalentWt: calculatedEquivalentWt.toFixed(3),
        totalAmt: calculatedTotalAmt.toFixed(2),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        equivalentWt: "",
        totalAmt: "",
      }));
    }
  }, [formData.rate, formData.purity, formData.netWt]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.material.trim()) {
      newErrors.material = "Material is required";
    }

    if (!formData.kt.trim()) {
      newErrors.kt = "Kt is required";
    }

    // if (rateLoading) {
    //   newErrors.rate = "Please wait for rate to load";
    // } else if (!formData.rate.trim() || isNaN(Number(todayGoldRate))) {
    //   newErrors.rate = "Rate could not be fetched. Please select a valid karat.";
    // } else if (rateError && Number(formData.rate) < 0) {
    //   newErrors.rate = "Failed to fetch rate. Please try selecting karat again.";
    // }

    const purity = Number(formData.purity);
    if (!formData.purity.trim() || purity <= 0 || purity > 100) {
      newErrors.purity = "Purity is required";
    }

    const netWt = Number(formData.netWt);
    if (!formData.netWt.trim() || netWt <= 0) {
      newErrors.netWt = "Net weight is required";
    } else if (netWt > 10000) {
      newErrors.netWt = "Net weight cannot exceed 10,000 gms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to reset form with 24KT rate
  const resetFormWithDefaultKarat = () => {
    const defaultKarat = karatList.find(karat => karat.text === "24KT");
    setFormData({
      material: "Gold",
      kt: "24KT",
      rate: "",
      purity: "",
      netWt: "",
      equivalentWt: "",
      totalAmt: "",
      note: "",
    });
    setErrors({});

    // Fetch rate for 24KT after reset
    if (defaultKarat) {
      getOldGoldRateByKaratId(defaultKarat.id);
    }
  };

  const handleAdd = () => {
    if (!validateForm()) {
      return;
    }
    const karat24 = karatList.find(kt => kt.text === "24KT");
    const karat24Id: string = String(karat24?.id);
    const newEntry: OldGoldEntry = {
      id: editingIndex !== null ? entries[editingIndex].id : Date.now().toString(),
      material: formData.material,
      kt: '24KT',
      rate: Number(todayGoldRate),
      purity: Number(formData.purity),
      netWt: Number(formData.netWt),
      equivalentWt: Number(formData.equivalentWt),
      totalAmt: Number(formData.totalAmt),
      note: formData.note,
    };

    const updatedEntries = editingIndex !== null
      ? entries.map((entry, index) => index === editingIndex ? newEntry : entry)
      : [...entries, newEntry];

    setEntries(updatedEntries);
    if (editingIndex !== null) setEditingIndex(null);

    // Update context with new entries for real-time calculation
    const totalAmount = updatedEntries.reduce((sum, entry) => sum + entry.totalAmt, 0);
    const totalWeight = updatedEntries.reduce((sum, entry) => sum + entry.netWt, 0);

    actions.setOldGoldEntries(updatedEntries);
    actions.setOldGold({ amount: totalAmount, weight: totalWeight });

    // Reset form with default karat and fetch rate
    resetFormWithDefaultKarat();
    onHide()
  };

  const handleEdit = (index: number) => {
    const entry = entries[index];
    setFormData({
      material: entry.material,
      kt: entry.kt,
      rate: todayGoldRate.toString(),
      purity: entry.purity.toString(),
      netWt: entry.netWt.toString(),
      equivalentWt: entry.equivalentWt.toString(),
      totalAmt: entry.totalAmt.toString(),
      note: entry.note,
    });
    setEditingIndex(index);
  };

  const handleDelete = (index: number) => {
    const updatedEntries = entries.filter((_, i) => i !== index);
    setEntries(updatedEntries);

    // Update context with calculated totals
    // Calculate totals
    const totalAmount = updatedEntries.reduce((sum, entry) => sum + entry.totalAmt, 0);
    const totalWeight = updatedEntries.reduce((sum, entry) => sum + entry.netWt, 0);

    if (updatedEntries.length > 0) {
      actions.setOldGoldEntries(updatedEntries);
      actions.setOldGold({ amount: totalAmount, weight: totalWeight });

      const currentExchangeAmt = state.voucherDetails?.exchangeAmt || 0;

      actions.updateVoucherDetails({
        oldGoldAmt: totalAmount,
        exchangeAmt: currentExchangeAmt,
        voucherAmt: totalAmount + currentExchangeAmt,
      });

    } else {
      actions.setOldGoldEntries([]);
      actions.setOldGold(null);

      const currentExchangeAmt = state.voucherDetails?.exchangeAmt || 0;

      actions.updateVoucherDetails({
        oldGoldAmt: 0,
        exchangeAmt: currentExchangeAmt,
        voucherAmt: 0 + currentExchangeAmt,
      });
    }


    if (editingIndex === index) {
      setEditingIndex(null);
      resetFormWithDefaultKarat();
    }
  };

  const handleSave = () => {
    const totalAmount = entries.reduce((sum, entry) => sum + entry.totalAmt, 0);
    const totalWeight = entries.reduce((sum, entry) => sum + entry.netWt, 0);

    if (entries.length > 0) {
      actions.setOldGoldEntries(entries);
      actions.setOldGold({ amount: totalAmount, weight: totalWeight });
    } else {
      actions.setOldGoldEntries([]);
      actions.setOldGold(null);
    }

    onHide();
  };

  const handleClose = () => {
    resetFormWithDefaultKarat();
    setEditingIndex(null);
    onHide();
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }

    // Since karat is fixed to 24KT, we don't need this logic anymore
    // but keeping it for future flexibility
    if (field === "kt" && value) {
      const selectedKarat = karatList.find(karat => karat.text === value);
      if (selectedKarat) {
        getOldGoldRateByKaratId(selectedKarat.id);
      }
    }
  };


  return (
    <Modal show={show} onHide={handleClose} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="text-secondary fw-normal">
          {/* <i className="bx bx-crown me-2"></i> */}
          Old Gold Purchase Entry
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Show API errors */}
        {karatError && (
          <Alert variant="warning" className="mb-3">
            <i className="bx bx-error-circle me-2"></i>
            Failed to load karat options: {karatError}. Using default options.
          </Alert>
        )}

        {/* Old Gold Entry Form */}
        <Card className="shadow-sm border-0 mb-4">
          <Card.Header className="bg-white border-0 pb-0">
            {/* <div className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0 text-dark fw-bold">
                {editingIndex !== null ? 'Edit Old Gold Entry' : 'Add New Old Gold Entry'}
              </h6>
              {editingIndex !== null && (
                <Badge bg="warning" className="px-3 py-2">
                  <i className="bx bx-edit me-1"></i>
                  Editing Mode
                </Badge>
              )}
            </div> */}
          </Card.Header>
          <Card.Body className="pt-3">
            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-diamond me-1 text-warning"></i> */}
                    Material <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.material}
                    onChange={(e) => updateField("material", e.target.value)}
                    isInvalid={!!errors.material}
                    size="sm"
                  >
                    <option value="Gold">Gold</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.material}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-certification me-1 text-info"></i> */}
                    Karat <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={formData.kt}
                    onChange={(e) => updateField("kt", e.target.value)}
                    isInvalid={!!errors.kt}
                    size="sm"
                    disabled={true}
                    style={{
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      backgroundImage: 'none',
                      cursor: 'not-allowed'
                    }}
                  >
                    <option value="24KT">24KT</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.kt}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-rupee me-1 text-success"></i> */}
                    Rate per gram <span className="text-danger">*</span>
                    {/* {rateLoading && (
                      <span className="text-primary ms-2">
                        <i className="bx bx-loader-alt bx-spin"></i>
                        <small className="ms-1">Fetching...</small>
                      </span>
                    )} */}
                  </Form.Label>
                  <Form.Control
                    type="text"

                    value={todayGoldRate}
                    readOnly
                    disabled
                    size="sm"
                    isInvalid={!!errors.rate}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.rate}
                  </Form.Control.Feedback>
                  {rateError && (
                    <Form.Text className="text-danger small" style={{ fontSize: "8px" }}>
                      <i className="bx bx-error-circle me-1"></i>
                      {rateError}
                    </Form.Text>
                  )}
                  {/* <Form.Text className="text-muted small" style={{ fontSize: "8px" }}>
                    Auto-fetched based on karat selection
                  </Form.Text> */}
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    Purity (%) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="0"
                    value={formData.purity}
                    // onChange={(e) => updateField("purity", e.target.value)}
                    onChange={(e) => {
                      const val = e.target.value;
                      const numVal = parseFloat(val);
                      if (
                        val === "" ||
                        (/^\d{0,3}(\.\d{0,2})?$/.test(val) && (!isNaN(numVal) && numVal <= 100))
                      ) {
                        updateField("purity", val);
                      }
                    }}
                    isInvalid={!!errors.purity}
                    min="0"
                    max="100"
                    step="0.01"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.purity}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-weight me-1 text-info"></i> */}
                    Net Wt (gm) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="0.000"
                    value={formData.netWt}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        updateField("netWt", val);
                        return;
                      }
                      const regex = /^\d{1,5}(\.\d{0,3})?$/;
                      if (regex.test(val)) {
                        if (Number(val) <= 10000) {
                          updateField("netWt", val);
                        }
                      }
                    }}
                    isInvalid={!!errors.netWt}
                    min="0"
                    max="10000"
                    step="0.001"
                    size="sm"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.netWt}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-calculator me-1 text-secondary"></i> */}
                    Equivalent Wt (gm)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      formData.equivalentWt ? `${formData.equivalentWt} gms` : ""
                    }
                    readOnly
                    disabled
                    size="sm"
                  />
                  <Form.Text className="text-muted small">
                    Auto-calculated
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-money me-1 text-success"></i> */}
                    Total Amount (₹)
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      formData.totalAmt
                        ? `₹${Number(formData.totalAmt).toLocaleString()}`
                        : ""
                    }
                    readOnly
                    disabled
                    size="sm"
                  />
                  <Form.Text className="text-muted small">
                    Auto-calculated
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label className="text-dark fw-semibold mb-2">
                    {/* <i className="bx bx-note me-1 text-muted"></i> */}
                    Note
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="note"
                    value={formData.note}
                    onChange={(e) => updateField("note", e.target.value)}
                    maxLength={150}
                    size="sm"
                    onInput={(e: any) => {
                      let value = e.target.value.replace(/\s+/g, ' ');
                      if (value.startsWith(' ')) {
                        value = value.trimStart();
                      }
                      e.target.value = value;
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-center">
                <div className="d-grid w-100">
                  <Button
                    variant={editingIndex !== null ? "warning" : "success"}
                    onClick={handleAdd}
                    className="fw-semibold py-1"
                    size="sm"
                  // disabled={
                  //   !todayGoldRate ||
                  //   !formData.purity ||
                  //   !formData.netWt ||
                  //   rateLoading
                  // }
                  >
                    {/* <i
                      className={`bx ${editingIndex != null ? "bx-edit" : "bx-plus"} me-2`}
                    ></i> */}
                    {editingIndex != null ? "Update Entry" : "Add Entry"}
                  </Button>
                  {editingIndex != null && (
                    <Button
                      size="sm"
                      className="mt-2 add-btn"
                      onClick={() => {
                        setEditingIndex(null);
                        resetFormWithDefaultKarat();
                      }}
                    >
                      {/* <i className="bx bx-x me-1"></i> */}
                      Cancel Edit
                    </Button>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {entries.length > 0 && (
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body className="p-0">
              <DataGrid
                dataSource={entries}
                showBorders
                showColumnLines
                showRowLines
                allowColumnReordering
                allowColumnResizing
                columnAutoWidth
                rowAlternationEnabled
                keyExpr="id"
              >
                <Column
                  caption="Actions"
                  width={120}
                  cellRender={({ data }) => (
                    <div className="d-flex gap-1">
                      {/* <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(entries.findIndex(entry => entry.id === data.id))}
                        title="Edit"
                      >
                        <i className="bx bx-edit"></i>
                      </Button> */}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(entries.findIndex(entry => entry.id === data.id))}
                        title="Delete"
                      >
                        <i className="bx bx-trash"></i>
                      </Button>
                    </div>
                  )}
                />
                <Column
                  caption="Material"
                  dataField="material"
                />

                <Column caption="Kt" dataField="kt" />
                <Column caption="Rate" dataField="rate" format="fixedPoint" />
                <Column caption="Purity (%)" dataField="purity" />
                <Column caption="Net Wt" dataField="netWt" />
                <Column caption="Total Amt" dataField="totalAmt" />
                <Column caption="Note" dataField="note" width={200} />


              </DataGrid>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-white border-top">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="text-muted small">
            <i className="bx bx-info-circle me-1"></i>
            {entries.length === 0 ? 'Add entries to save' : `Ready to save ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}`}
          </div>
          <div className="d-flex gap-2">
            <Button onClick={handleClose} className="px-4 add-btn">
              {/* <i className="bx bx-x me-1"></i> */}
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={entries.length === 0}
              className="px-4 fw-semibold submit-btn"
            >
              {/* <i className="bx bx-check me-1"></i> */}
              Save
              {/* {entries.length > 0 && `(${entries.length})`} */}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};